import { createReadingDraft, getEffectiveLevel, getReading, getUserLevelProfile, resolveReadingLevel, updateReading } from "../db";
import { LEVEL_LABELS } from "../level";
import { navigate } from "../router";
import type { ReadingLevel, Route } from "../types";
import { uiConfirm } from "../ui/dialog";
import { getViewEl } from "../ui/layout";
import { attachTextareaSelectionAsk } from "../ui/selectionAsk";
import { esc, toast } from "../ui/toast";

function textChangedSignificantly(
  original: { text: string; selection: string | null },
  next: { text: string; selection: string | null },
): boolean {
  return original.text.trim() !== next.text.trim() || (original.selection ?? "") !== (next.selection ?? "");
}

export async function renderReadPage(route: Route): Promise<void> {
  const view = getViewEl();
  if (!view) {
    return;
  }

  const [profile, effectiveLevel] = await Promise.all([getUserLevelProfile(), getEffectiveLevel()]);
  const editingId = route.readingId;
  const existing = editingId ? await getReading(editingId) : undefined;

  if (editingId && !existing) {
    toast("阅读记录不存在");
    navigate("library");
    return;
  }

  const isEdit = Boolean(existing);

  view.innerHTML = `
    <div class="page-head">
      <div>
        <h1>${isEdit ? "编辑阅读" : "阅读"}</h1>
        <p class="lead">${
          isEdit
            ? "修改标题、原文或选中段落。若正文有变动，保存时可选择清除已有诊断。"
            : "粘贴一段读不懂的英文。下一步会由 AI 诊断卡点，并分步带你读懂。"
        }</p>
      </div>
      ${isEdit ? `<button type="button" class="btn" id="btnBackLibrary">返回文库</button>` : ""}
    </div>
    <section class="panel read-panel">
      ${
        isEdit
          ? `<label class="field">
        <span>标题</span>
        <input type="text" id="readTitle" maxlength="120" placeholder="阅读标题" />
      </label>`
          : ""
      }
      <label class="field">
        <span>英文原文</span>
        <textarea id="readText" rows="12" placeholder="Paste English text here…"></textarea>
      </label>
      <label class="field">
        <span>可选：选中/粘贴不懂的段落</span>
        <textarea id="readSelection" rows="4" placeholder="Leave empty to analyze the whole text."></textarea>
      </label>
      <div class="field-row">
        <label class="field field-inline">
          <span>你的水平</span>
          <select id="readLevel">
            <option value="auto">自动（跟随评估）</option>
            <option value="beginner">初级</option>
            <option value="intermediate">中级</option>
            <option value="advanced">高级</option>
          </select>
        </label>
        <p class="muted">当前评估：${esc(LEVEL_LABELS[effectiveLevel])}${profile.levelReason ? ` · ${esc(profile.levelReason.slice(0, 40))}${profile.levelReason.length > 40 ? "…" : ""}` : ""}</p>
      </div>
      <div class="actions">
        <button type="button" class="btn btn-primary" id="btnStartRead">
          ${isEdit ? "保存修改" : "保存并开始分析"}
        </button>
        ${isEdit ? `<button type="button" class="btn" id="btnCancelEdit">取消</button>` : ""}
      </div>
    </section>
  `;

  const levelEl = view.querySelector<HTMLSelectElement>("#readLevel");
  if (levelEl) {
    levelEl.value = existing?.level ?? "auto";
  }

  const textEl = view.querySelector<HTMLTextAreaElement>("#readText");
  const selectionEl = view.querySelector<HTMLTextAreaElement>("#readSelection");
  const titleEl = view.querySelector<HTMLInputElement>("#readTitle");

  if (existing) {
    if (titleEl) {
      titleEl.value = existing.title;
    }
    if (textEl) {
      textEl.value = existing.text;
    }
    if (selectionEl) {
      selectionEl.value = existing.selection ?? "";
    }
  }

  const readingAskLevel = await resolveReadingLevel(existing?.level ?? "auto");
  const editingReadingId = editingId ?? null;
  const detachTextAsk: Array<() => void> = [];
  if (textEl) {
    detachTextAsk.push(
      attachTextareaSelectionAsk({
        textarea: textEl,
        getReadingContext: () => textEl.value,
        effectiveLevel: readingAskLevel,
        readingId: editingReadingId,
      }),
    );
  }
  if (selectionEl) {
    detachTextAsk.push(
      attachTextareaSelectionAsk({
        textarea: selectionEl,
        getReadingContext: () => textEl?.value ?? "",
        effectiveLevel: readingAskLevel,
        readingId: editingReadingId,
      }),
    );
  }

  const cleanupTextAsk = () => {
    for (const detach of detachTextAsk) {
      detach();
    }
  };

  view.querySelector<HTMLButtonElement>("#btnBackLibrary")?.addEventListener("click", () => {
    cleanupTextAsk();
    navigate("library");
  });

  view.querySelector<HTMLButtonElement>("#btnCancelEdit")?.addEventListener("click", () => {
    cleanupTextAsk();
    navigate("library");
  });

  view.querySelector<HTMLButtonElement>("#btnStartRead")?.addEventListener("click", async () => {
    const text = textEl?.value.trim() ?? "";
    const selection = selectionEl?.value.trim() ?? "";
    const level = (levelEl?.value ?? "auto") as ReadingLevel;

    if (!text) {
      toast("请先粘贴英文原文");
      return;
    }

    try {
      if (existing && editingId) {
        const title =
          titleEl?.value.trim() ||
          text
            .split(/\n/)
            .map((line) => line.trim())
            .find(Boolean)
            ?.slice(0, 60) ||
          existing.title;

        const next = { text, selection: selection || null };
        const hasDiagnosis = Boolean(existing.diagnosisSummary?.trim());
        const contentChanged = textChangedSignificantly(existing, next);
        let resetDiagnosis = false;

        if (contentChanged && hasDiagnosis) {
          resetDiagnosis = await uiConfirm({
            title: "清除诊断？",
            message:
              "原文或选中段落已修改。是否清除已有诊断与学习步骤，并重新从草稿开始？\n\n选择「确定」将清除诊断；选择「取消」仅更新文字，保留现有进度。",
            confirmLabel: "清除诊断",
          });
        }

        await updateReading(
          editingId,
          { title, text, selection: selection || null, level },
          { resetDiagnosis },
        );
        cleanupTextAsk();
        toast(resetDiagnosis ? "已保存，诊断已清除" : "已保存修改");
        navigate("diagnosis", null, editingId);
        return;
      }

      const reading = await createReadingDraft({
        text,
        selection: selection || null,
        level,
      });
      cleanupTextAsk();
      toast("已保存，开始 AI 诊断");
      navigate("diagnosis", null, reading.id);
    } catch (err) {
      toast(err instanceof Error ? err.message : "保存失败");
    }
  });
}
